import orderBy from "lodash/orderBy";
import React, { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ThemeContext } from "../../contexts/ThemeContext";
import {
  getProductSKUFromTags,
  ProductTagPrefix,
} from "../../ecommerce/product";
import { CartItem, CartSummary } from "../../types/shopify-components";
import LocaleLink from "../LocaleLink";

import CartLineItem from "./CartLineItem";

export const TEST_ID_CART_CONTENT_TOTAL_COUNT_OF_ITEMS =
  "cart-content-total-count-of-items";
export const TEST_ID_CART_CONTENT_EMPTY_MESSAGE = "cart-content-empty-message";
export const TEST_ID_CART_CONTENT_CONTINUE_SHOPPING =
  "cart-content-continue-shopping";

interface CartContentProps {
  items: CartItem[];
  pastItemIds: string[];
  summary: CartSummary;
}

const CartContent: React.FC<CartContentProps> = ({
  items,
  pastItemIds,
  summary,
}) => {
  const { styles } = useContext(ThemeContext);
  const { formatMessage } = useIntl();

  const visibleItems = orderBy(items, [
    item => item.merchandise.title,
    item => item.merchandise.validityDate?.toISODate(),
  ]).reduce<JSX.Element[]>((elements, item) => {
    const { COMPOSITE_CHILD_PRODUCT, COMPOSITE_PARENT_PRODUCT } =
      ProductTagPrefix;
    const data = getProductSKUFromTags(item.merchandise.tags);

    if (data?.type === COMPOSITE_CHILD_PRODUCT) {
      return elements;
    }

    let compositeChilds;
    if (data?.type === COMPOSITE_PARENT_PRODUCT) {
      compositeChilds = items.filter(innerItem => {
        const innerData = getProductSKUFromTags(innerItem.merchandise.tags);

        return (
          innerData?.type === COMPOSITE_CHILD_PRODUCT &&
          innerData.sku === data.sku
        );
      });
    }

    elements.push(
      <li
        key={item.cartLineId}
        className={elements.length === 0 ? "" : "bt b--dark-gray pt3 mt3"}
      >
        <CartLineItem
          isPastItem={pastItemIds.includes(item.merchandise.id)}
          item={item}
          connectedItems={compositeChilds}
        />
      </li>,
    );
    return elements;
  }, []);
  return (
    <div
      className={`h-100 bg-white pv4 ph3 ${summary.itemCount === 0 && "vh-30"}`}
    >
      <div className="flex items-center justify-between mb3">
        <h1 className={`${styles.heading1} ma0`}>
          <FormattedMessage id="cart.yourCart" />
        </h1>
        <span
          className={`${styles.paragraph2} pr2`}
          data-testid={TEST_ID_CART_CONTENT_TOTAL_COUNT_OF_ITEMS}
        >
          <FormattedMessage
            id="cart.numberOfItemsInCart"
            values={{ count: summary.itemCount }}
          />
        </span>
      </div>
      <section className="mt4 pt1">
        {visibleItems.length ? (
          <ul className="list ma0 pa0">{visibleItems}</ul>
        ) : (
          <div
            className={`${styles.paragraph3} nested-contained-copy-separator`}
          >
            <p data-testid={TEST_ID_CART_CONTENT_EMPTY_MESSAGE}>
              <FormattedMessage id="cart.isEmpty" />
            </p>
          </div>
        )}
      </section>
      <LocaleLink slug={formatMessage({ id: "appPath.products" })}>
        <a
          className={`${styles.secondaryButton} mt4 w-100 w-60-m w-40-l`}
          data-testid={TEST_ID_CART_CONTENT_CONTINUE_SHOPPING}
        >
          <FormattedMessage id="cart.continueShopping" />
        </a>
      </LocaleLink>
    </div>
  );
};

export default CartContent;
